import React, { useState } from 'react';
import { Button } from '@/src/modules/components/Button';
import { Icons } from '@/src/modules/components/Icons';
import { StepProps } from '@/src/types';
import { motion, AnimatePresence } from 'motion/react';
import { useNavigate } from 'react-router-dom';
import { createInvitation, publishInvitation, getInvitationStatus } from '@/src/infrastructure/lib/api';
import { usePublishEntitlement } from '@/src/modules/entitlements/usePublishEntitlement';
import { InvitationPublishPreviewCard } from './InvitationPublishPreviewCard';
import { DraftPreviewModal } from './DraftPreviewModal';
import { generateShareMessage } from '@/src/infrastructure/lib/share';
import { isAiNativeExperience } from './wizard-routing';

type PublishPhase = 'idle' | 'creating' | 'publishing' | 'polling' | 'done' | 'error';

function formatDate(iso: string | undefined) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-US', {
    weekday: 'short', year: 'numeric', month: 'long', day: 'numeric',
  });
}

function formatTime(iso: string | undefined) {
  if (!iso) return '';
  return new Date(iso).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
}

export function StepPublish({ onBack, data, draftId, saveDraftNow }: StepProps) {
  const navigate = useNavigate();
  const [phase, setPhase] = useState<PublishPhase>('idle');
  const [publishError, setPublishError] = useState('');
  const [publishedUrl, setPublishedUrl] = useState('');
  // Publishing rights read model. We surface this so the wizard frames the
  // publish action in terms of Publish Credits (the primary entitlement) rather
  // than the legacy "1 active invitation" plan limit. Reads the entitlement
  // view, never raw credit columns, so the future subscription provider needs
  // no change here.
  // reload() is called after a successful publish so the credit count displayed
  // in the UI decrements immediately without requiring a page refresh.
  const { data: entitlement, loading: entitlementLoading, reload: reloadEntitlement } = usePublishEntitlement();

  const handlePublish = async () => {
    setPublishError('');
    setPhase('creating');

    let invitationId: string | null = null;

    try {
      // The wizard autosaves a server-side DRAFT as the user works. Flush any
      // pending change so we publish the latest content, then publish that
      // draft. Fall back to a fresh create only if no draft exists yet (e.g.
      // autosave never ran).
      invitationId = saveDraftNow ? await saveDraftNow() : draftId ?? null;
      if (!invitationId) {
        const created = await createInvitation({
          templateId: data.templateId!,
          occasionType: data.occasionType!,
          eventName: data.eventName!,
          eventDate: data.eventDate!,
          eventVenue: data.eventVenue!,
          hostName: data.hostName!,
          customMessage: data.customMessage,
          rsvpLink: data.rsvpLink,
          mediaUrls: data.mediaUrls ?? [],
          customData: data.customData,
        });
        invitationId = created.id;
      }
    } catch (err) {
      setPhase('error');
      setPublishError(err instanceof Error ? err.message : 'Failed to create invitation.');
      return;
    }

    if (!invitationId) {
      setPhase('error');
      setPublishError('Could not prepare the invitation. Please try again.');
      return;
    }

    setPhase('publishing');

    try {
      await publishInvitation(invitationId);
    } catch (err) {
      setPhase('error');
      setPublishError(err instanceof Error ? err.message : 'Failed to submit for publishing.');
      return;
    }

    setPhase('polling');

    // Poll every 2 seconds until PUBLISHED or FAILED.
    //
    // AI-native experience templates (Barbie/Iron Man/Dark Knight and other
    // exp-* templates) run a real per-ceremony AI image generation pass on the
    // backend (up to 3 attempts per ceremony image, plus BullMQ's own 3 job
    // retries) before the invitation becomes PUBLISHED — that can legitimately
    // take well over a minute. The old flat 60s deadline gave up on these while
    // the backend job was often still running (and would have succeeded a bit
    // later), surfacing "Publishing timed out" for a publish that wasn't
    // actually broken. Non-AI templates (render → compress → deploy) are fast
    // and keep the original budget.
    const isAiNative = isAiNativeExperience(data);
    const deadline = Date.now() + (isAiNative ? 240_000 : 60_000);
    const poll = async (): Promise<void> => {
      if (Date.now() > deadline) {
        setPhase('error');
        setPublishError(
          isAiNative
            ? 'Publishing is taking longer than usual (AI-generated templates can take a few minutes). It may still complete — check your dashboard for status in a minute.'
            : 'Publishing timed out. Check your dashboard for status.'
        );
        return;
      }
      const status = await getInvitationStatus(invitationId);
      if (status.status === 'PUBLISHED') {
        setPublishedUrl(status.hostedUrl ?? '');
        setPhase('done');
        // Refresh the entitlement so the credit count shown to the user
        // reflects the newly consumed credit — no page reload required.
        void reloadEntitlement();
        return;
      }
      if (status.status === 'FAILED') {
        setPhase('error');
        setPublishError(status.failureReason ?? 'Publishing failed. Please try again.');
        return;
      }
      await new Promise(r => setTimeout(r, 2000));
      return poll();
    };

    try {
      await poll();
    } catch (err) {
      setPhase('error');
      setPublishError(err instanceof Error ? err.message : 'Unexpected error during publishing.');
    }
  };

  const handleSaveDraft = async () => {
    setPublishError('');
    setPhase('creating');
    try {
      // The draft is already autosaved server-side; just flush the latest edit
      // (or create it if autosave never ran) and head to the Drafts dashboard.
      if (saveDraftNow) {
        await saveDraftNow();
      } else {
        await createInvitation({
          templateId: data.templateId!,
          occasionType: data.occasionType!,
          eventName: data.eventName!,
          eventDate: data.eventDate!,
          eventVenue: data.eventVenue!,
          hostName: data.hostName!,
          customMessage: data.customMessage,
          rsvpLink: data.rsvpLink,
          mediaUrls: data.mediaUrls ?? [],
          customData: data.customData,
        });
      }
      navigate('/dashboard/drafts');
    } catch (err) {
      setPhase('error');
      setPublishError(err instanceof Error ? err.message : 'Failed to save draft.');
    }
  };

  const [showPreview, setShowPreview] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(publishedUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const isPublishing = phase === 'creating' || phase === 'publishing' || phase === 'polling';
  const isDone = phase === 'done';
  const shareText = generateShareMessage(data, publishedUrl);

  return (
    <div className="w-full max-w-3xl flex flex-col relative">
      <div className="mb-12">
        <h1 className="text-4xl font-bold text-primary mb-2">Ready to Publish</h1>
        <p className="text-on-surface-variant font-medium">Review your invitation details before sending it out.</p>
      </div>

      <div className="grid gap-12">
        {/* Summary */}
        <section className="bg-surface-low/50 rounded-3xl p-8 border border-outline-variant/20 flex flex-col md:flex-row gap-8 items-center md:items-start text-center md:text-left">
          <div
            className="w-32 h-44 bg-primary/10 rounded-2xl overflow-hidden shadow-inner flex-shrink-0 flex items-center justify-center cursor-pointer hover:opacity-80 transition-opacity group relative"
            onClick={() => setShowPreview(true)}
            title="Preview invitation"
          >
            <Icons.Calendar size={48} className="text-primary/30 group-hover:opacity-0 transition-opacity" />
            <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-primary/20 rounded-2xl">
              <Icons.Eye size={28} className="text-primary" />
            </div>
          </div>

          <div className="flex flex-col flex-1 gap-3">
            <h2 className="text-3xl font-bold text-brand-deep">{data.eventName}</h2>
            <div className="flex flex-wrap justify-center md:justify-start gap-x-6 gap-y-2">
              <div className="flex items-center gap-2 text-on-surface-variant text-sm font-medium">
                <Icons.Calendar size={16} className="text-primary" />
                {formatDate(data.eventDate)} at {formatTime(data.eventDate)}
              </div>
              <div className="flex items-center gap-2 text-on-surface-variant text-sm font-medium">
                <Icons.MapPin size={16} className="text-primary" />
                {data.eventVenue}
              </div>
            </div>
            <div className="flex items-center justify-center md:justify-start gap-2 text-on-surface-variant text-sm font-medium mt-1">
              <Icons.Users size={16} className="text-primary" />
              <span>Hosted by {data.hostName}</span>
            </div>
            {data.customMessage && (
              <p className="text-sm text-on-surface-variant italic mt-1 line-clamp-2">"{data.customMessage}"</p>
            )}
            <div className="pt-2 flex items-center justify-center md:justify-start gap-3">
              <button
                type="button"
                onClick={() => setShowPreview(true)}
                className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl border border-outline-variant text-xs font-semibold text-on-surface hover:bg-surface-low transition-colors"
              >
                <Icons.Eye size={14} className="text-primary" />
                Preview Template
              </button>
              <button
                type="button"
                onClick={onBack}
                className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl border border-primary/30 bg-primary/5 text-xs font-semibold text-primary hover:bg-primary/10 transition-colors"
              >
                <Icons.Edit3 size={14} />
                Change Template / Details
              </button>
            </div>
          </div>
        </section>

        {/* Publish entitlement notice — credits are the primary publish gate.
            We never show the legacy "Free plan allows only 1 active invitation"
            message here; instead we tell the user exactly what publishing costs. */}
        {!entitlementLoading && entitlement && (
          entitlement.canPublish ? (
            <div className="rounded-2xl bg-primary/5 border border-primary/20 px-6 py-4 flex items-center gap-3 text-sm">
              <Icons.Upload size={18} className="text-primary flex-shrink-0" />
              <p className="text-on-surface-variant font-medium">
                This publish will consume <span className="font-bold text-on-surface">1 Publish Credit</span>.{' '}
                <span className="text-on-surface-variant">
                  Available: {entitlement.remainingCredits} {entitlement.remainingCredits === 1 ? 'credit' : 'credits'}.
                </span>
              </p>
            </div>
          ) : (
            <div className="rounded-2xl bg-status-failed/10 border border-status-failed/30 px-6 py-4 flex items-center gap-3 text-sm">
              <Icons.Upload size={18} className="text-status-failed flex-shrink-0" />
              <p className="text-status-failed font-medium">
                No Publish Credits available. Contact the InviteCloud team for beta access — you can still save this invitation as a draft.
              </p>
            </div>
          )
        )}

        {publishError && (
          <div className="rounded-2xl bg-status-failed/10 border border-status-failed/30 px-6 py-4 text-status-failed font-medium text-sm">
            {publishError}
          </div>
        )}

        <div className="pt-8 border-t border-outline-variant/20 flex flex-col sm:flex-row justify-between gap-4 mt-8">
          <div className="flex gap-3">
            <Button variant="outline" size="lg" onClick={onBack} className="px-10 rounded-2xl" disabled={isPublishing}>
              Back
            </Button>
            <Button
              variant="outline"
              size="lg"
              onClick={() => setShowPreview(true)}
              className="px-6 rounded-2xl flex items-center gap-2"
              disabled={isPublishing}
            >
              <Icons.Eye size={18} /> Preview
            </Button>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" size="lg" onClick={handleSaveDraft} className="px-10 rounded-2xl" disabled={isPublishing}>
              Save as Draft
            </Button>
            <Button
              size="lg"
              onClick={handlePublish}
              className="px-10 rounded-2xl flex items-center gap-2 shadow-lg shadow-primary/20"
              disabled={isPublishing || (!entitlementLoading && entitlement != null && !entitlement.canPublish)}
            >
              <Icons.Rocket size={20} />
              Publish Now
            </Button>
          </div>
        </div>
      </div>

      {/* Draft preview modal — available even without publish credits */}
      <DraftPreviewModal
        open={showPreview}
        data={{
          templateId: data.templateId ?? '',
          occasionType: data.occasionType ?? 'WEDDING',
          eventName: data.eventName,
          eventDate: data.eventDate,
          eventVenue: data.eventVenue,
          hostName: data.hostName,
          customMessage: data.customMessage,
          rsvpLink: data.rsvpLink,
          mediaUrls: data.mediaUrls,
          customData: data.customData as Record<string, unknown> | null | undefined,
        }}
        onClose={() => setShowPreview(false)}
        ctaLabel={entitlement?.canPublish ? 'Publish Now' : undefined}
        onCta={entitlement?.canPublish ? () => { setShowPreview(false); handlePublish(); } : undefined}
      />

      {/* Publishing overlay */}
      <AnimatePresence>
        {(isPublishing || isDone) && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-surface/95 backdrop-blur-xl z-[100] flex flex-col items-center justify-center p-8 text-center overflow-y-auto"
          >
            {isDone ? (
              <>
                <div className="w-20 h-20 mb-6 rounded-full bg-status-published/10 flex items-center justify-center">
                  <Icons.CheckCircle size={40} className="text-status-published" />
                </div>
                <h2 className="text-3xl font-bold text-brand-deep mb-2">Published!</h2>
                <p className="text-on-surface-variant font-medium mb-8">Your invitation is live and ready to share.</p>
                {publishedUrl && (
                  <>
                    <InvitationPublishPreviewCard data={data} publishedUrl={publishedUrl} />
                    <div className="flex flex-wrap justify-center gap-3 mt-6">
                      <Button
                        variant={copied ? 'secondary' : 'outline'}
                        onClick={handleCopyLink}
                        className="flex items-center gap-2 transition-all"
                      >
                        {copied
                          ? <><Icons.Check size={16} className="text-status-published" /> Copied!</>
                          : <><Icons.Copy size={16} /> Copy Link</>
                        }
                      </Button>
                      <a
                        href={`https://wa.me/?text=${encodeURIComponent(shareText)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Button variant="outline" className="flex items-center gap-2">
                          <Icons.WhatsApp size={16} /> WhatsApp
                        </Button>
                      </a>
                      <a
                        href={`mailto:?subject=${encodeURIComponent(data.eventName ?? 'Invitation')}&body=${encodeURIComponent(publishedUrl)}`}
                      >
                        <Button variant="outline" className="flex items-center gap-2">
                          <Icons.Mail size={16} /> Email
                        </Button>
                      </a>
                    </div>
                  </>
                )}
                <Button onClick={() => navigate('/dashboard')} className="px-8 mt-6">
                  Back to Dashboard
                </Button>
              </>
            ) : (
              <>
                <div className="w-20 h-20 mb-8 relative">
                  <div className="absolute inset-0 rounded-full border-4 border-primary/10" />
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                    className="absolute inset-0 rounded-full border-4 border-primary border-t-transparent"
                  />
                </div>
                <h2 className="text-3xl font-bold text-brand-deep mb-4">
                  {phase === 'creating' ? 'Creating Invitation...' :
                   phase === 'publishing' ? 'Submitting for Publishing...' :
                   'Publishing Invitation...'}
                </h2>
                <p className="text-on-surface-variant font-medium max-w-sm">
                  {phase === 'polling'
                    ? 'Generating your unique link and preparing assets. This usually takes a few seconds.'
                    : 'Please wait...'}
                </p>
                <div className="mt-12 w-full max-w-xs bg-surface-low rounded-full h-2 overflow-hidden shadow-inner">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: phase === 'polling' ? '80%' : '40%' }}
                    transition={{ duration: 1.5, ease: 'easeInOut' }}
                    className="bg-primary h-full rounded-full"
                  />
                </div>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
